import { ref } from 'vue'

export interface ElevenLabsVoice {
    voice_id: string
    name: string
    category?: string
    labels?: Record<string, string>
    preview_url?: string
}

interface TTSSettings {
    useServerTTS: boolean
    selectedVoice: string
    speechRate: string
    speechPitch: string
    fallbackToWebSpeech: boolean
}

const TTS_SETTINGS_KEY = 'ttsSettingsElevenlabs_v1'
const DEFAULT_VOICE_ID = '21m00Tcm4TlvDq8ikWAM'

const isSpeaking = ref(false)
const isServerTTSAvailable = ref(false)
const voices = ref<SpeechSynthesisVoice[]>([])
const elevenLabsVoices = ref<ElevenLabsVoice[]>([])
const selectedVoice = ref<SpeechSynthesisVoice | null>(null)
const selectedElevenLabsVoice = ref<ElevenLabsVoice | null>(null)

function migrateRawSettings(parsed: unknown): TTSSettings {
    const base: TTSSettings = {
        useServerTTS: true,
        selectedVoice: DEFAULT_VOICE_ID,
        speechRate: '+0%',
        speechPitch: '+0Hz',
        fallbackToWebSpeech: true,
    }
    if (!parsed || typeof parsed !== 'object') {
        return base
    }
    const p = parsed as Record<string, unknown>
    return {
        useServerTTS: typeof p.useServerTTS === 'boolean' ? p.useServerTTS : base.useServerTTS,
        selectedVoice:
            typeof p.selectedVoice === 'string'
                ? p.selectedVoice.includes('Neural') || p.selectedVoice.includes('en-US-')
                    ? DEFAULT_VOICE_ID
                    : p.selectedVoice
                : base.selectedVoice,
        speechRate: typeof p.speechRate === 'string' ? p.speechRate : base.speechRate,
        speechPitch: typeof p.speechPitch === 'string' ? p.speechPitch : base.speechPitch,
        fallbackToWebSpeech:
            typeof p.fallbackToWebSpeech === 'boolean' ? p.fallbackToWebSpeech : base.fallbackToWebSpeech,
    }
}

const savedSettings = localStorage.getItem(TTS_SETTINGS_KEY)
let initial: TTSSettings
try {
    initial = savedSettings ? migrateRawSettings(JSON.parse(savedSettings)) : migrateRawSettings(null)
} catch {
    initial = migrateRawSettings(null)
}

const settings = ref<TTSSettings>(initial)

const initializeVoices = () => {
    voices.value = speechSynthesis.getVoices()
    selectedVoice.value =
        voices.value.find(
            (v) =>
                v.name.includes('Female') ||
                v.name.includes('Google US English') ||
                v.name.includes('Samantha')
        ) || voices.value[0] || null
}

function speechRateToElevenLabsSpeed(rate: string): number {
    const m = rate.match(/^([+-]?\d+)%$/)
    const pct = m ? parseInt(m[1], 10) : 0
    const speed = 1 + pct * 0.002
    return Math.min(1.2, Math.max(0.7, speed))
}

interface TtsCredentials {
    token: string
    model_id: string
    default_voice_id: string
    output_format: string
    tts_ws_origin: string
}

async function fetchTtsCredentials(): Promise<TtsCredentials> {
    const response = await fetch('/api/text_to_speech/tts_token', {
        method: 'POST',
        headers: {
            Accept: 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
        },
    })
    const json = await response.json()
    if (!json.success) {
        throw new Error(json.error || 'Failed to get TTS token')
    }
    return json.data as TtsCredentials
}

const loadElevenLabsVoices = async () => {
    try {
        const response = await fetch('/api/text_to_speech/popular_voices')
        if (response.ok) {
            const json = await response.json()
            if (json.success && json.data?.voices) {
                elevenLabsVoices.value = json.data.voices as ElevenLabsVoice[]

                if (settings.value.selectedVoice && elevenLabsVoices.value.length > 0) {
                    selectedElevenLabsVoice.value =
                        elevenLabsVoices.value.find((v) => v.voice_id === settings.value.selectedVoice) ||
                        elevenLabsVoices.value[0]
                }
            }
        }
    } catch (error) {
        console.error('Failed to load ElevenLabs voices:', error)
    }
}

const checkServerTTSAvailability = async () => {
    try {
        const response = await fetch('/api/text_to_speech/health')
        if (!response.ok) {
            isServerTTSAvailable.value = false
            return
        }
        const json = await response.json()
        isServerTTSAvailable.value = json.success === true
    } catch (error) {
        console.error('ElevenLabs TTS health check failed:', error)
        isServerTTSAvailable.value = false
    }
}

const speakWithServerTTS = async (text: string): Promise<boolean> => {
    try {
        const creds = await fetchTtsCredentials()
        const voiceId = settings.value.selectedVoice || creds.default_voice_id
        const origin = creds.tts_ws_origin.replace(/\/$/, '')
        const outputFormat = creds.output_format || 'mp3_44100_128'
        const q = new URLSearchParams({
            single_use_token: creds.token,
            output_format: outputFormat,
            model_id: creds.model_id,
        })
        const wsUrl = `${origin}/v1/text-to-speech/${voiceId}/stream-input?${q.toString()}`

        const chunks: Uint8Array[] = []

        await new Promise<void>((resolve, reject) => {
            const ws = new WebSocket(wsUrl)
            let settled = false

            const finishOk = () => {
                if (settled) {
                    return
                }
                settled = true
                resolve()
            }

            const finishErr = (msg: string) => {
                if (settled) {
                    return
                }
                settled = true
                reject(new Error(msg))
            }

            ws.onopen = () => {
                const speed = speechRateToElevenLabsSpeed(settings.value.speechRate)
                ws.send(
                    JSON.stringify({
                        text: ' ',
                        voice_settings: {
                            stability: 0.5,
                            similarity_boost: 0.8,
                            use_speaker_boost: true,
                            speed,
                        },
                        generation_config: { chunk_length_schedule: [120, 160, 250, 290] },
                    })
                )
                ws.send(JSON.stringify({ text, flush: true }))
                ws.send(JSON.stringify({ text: '' }))
            }

            ws.onmessage = (event) => {
                try {
                    const data = JSON.parse(event.data as string)
                    if (data.audio) {
                        const bin = atob(data.audio as string)
                        const bytes = new Uint8Array(bin.length)
                        for (let i = 0; i < bin.length; i++) {
                            bytes[i] = bin.charCodeAt(i)
                        }
                        chunks.push(bytes)
                    }
                    if (data.isFinal === true) {
                        ws.close()
                    }
                } catch {
                    /* ignore */
                }
            }

            ws.onerror = () => {
                finishErr('TTS WebSocket failed')
            }

            ws.onclose = () => {
                finishOk()
            }

            window.setTimeout(() => {
                if (!settled) {
                    finishErr('TTS timed out')
                    ws.close()
                }
            }, 60000)
        })

        if (chunks.length === 0) {
            return false
        }

        const audioBlob = new Blob(chunks as BlobPart[], { type: 'audio/mpeg' })
        const audioUrl = URL.createObjectURL(audioBlob)
        const audio = new Audio(audioUrl)

        audio.onloadstart = () => {
            isSpeaking.value = true
        }

        audio.onended = () => {
            isSpeaking.value = false
            URL.revokeObjectURL(audioUrl)
        }

        audio.onerror = () => {
            isSpeaking.value = false
            URL.revokeObjectURL(audioUrl)
        }

        await audio.play()
        return true
    } catch (error) {
        console.error('ElevenLabs TTS failed:', error)
        return false
    }
}

const speakWithWebSpeech = (text: string, options: Record<string, unknown> = {}) => {
    if ('speechSynthesis' in window) {
        speechSynthesis.cancel()

        const utterance = new SpeechSynthesisUtterance(text)

        if (selectedVoice.value) {
            utterance.voice = selectedVoice.value
        }

        utterance.rate = (options.rate as number) || 0.9
        utterance.pitch = (options.pitch as number) || 1.0
        utterance.volume = (options.volume as number) || 1.0

        utterance.onstart = () => {
            isSpeaking.value = true
        }

        utterance.onend = () => {
            isSpeaking.value = false
        }

        utterance.onerror = () => {
            isSpeaking.value = false
        }

        speechSynthesis.speak(utterance)
        return true
    }
    return false
}

const stopSpeaking = () => {
    if ('speechSynthesis' in window) {
        speechSynthesis.cancel()
    }
    isSpeaking.value = false
}

const speak = async (text: string, options: Record<string, unknown> = {}) => {
    stopSpeaking()

    if (settings.value.useServerTTS && isServerTTSAvailable.value) {
        const success = await speakWithServerTTS(text)
        if (success) {
            return
        }
    }

    if (settings.value.fallbackToWebSpeech) {
        speakWithWebSpeech(text, options)
    }
}

const readResponseAloud = async (response: string) => {
    await speak(response)
}

const previewVoice = async (voiceId: string) => {
    const testText = 'Hello, this is a preview of this voice.'
    const original = settings.value.selectedVoice

    settings.value.selectedVoice = voiceId
    await speak(testText)

    window.setTimeout(() => {
        settings.value.selectedVoice = original
    }, 2000)
}

const updateSettings = (newSettings: Partial<TTSSettings>) => {
    settings.value = { ...settings.value, ...newSettings }
    localStorage.setItem(TTS_SETTINGS_KEY, JSON.stringify(settings.value))
}

const initVoiceSynthesis = async () => {
    if (speechSynthesis.onvoiceschanged !== undefined) {
        speechSynthesis.onvoiceschanged = initializeVoices
    }
    initializeVoices()

    await checkServerTTSAvailability()
    if (isServerTTSAvailable.value) {
        await loadElevenLabsVoices()
    }

    if (settings.value.selectedVoice && elevenLabsVoices.value.length > 0) {
        selectedElevenLabsVoice.value =
            elevenLabsVoices.value.find((v) => v.voice_id === settings.value.selectedVoice) ||
            elevenLabsVoices.value[0]
    }
}

export const useVoiceSynthesis = () => {
    return {
        isSpeaking,
        isServerTTSAvailable,
        voices,
        elevenLabsVoices,
        selectedVoice,
        selectedElevenLabsVoice,
        settings,
        speakWithServerTTS,
        speakWithWebSpeech,
        speak,
        stopSpeaking,
        readResponseAloud,
        previewVoice,
        updateSettings,
        initializeVoices,
        loadElevenLabsVoices,
        checkServerTTSAvailability,
        initVoiceSynthesis,
    }
}
